inherit("Modules/Carousel", {
	parent: `Module`,
	requires: [`Events`, `Utils/CollectionUtils`],
	defaults: {
		cart: null,
		eventId: null,
		mode: `legacy`, // legacy | ux3
		render: true,
	},
	prepare(setup) {
		this.CollectionUtils = this[`Utils/CollectionUtils`];

		const { Events, data } = this;
		const {
			blurb,
			cart,
			eventId,
			items,
			layout,
			mobileLayout,
			mobileOrientation,
			mode,
			moreInfoHidden,
			orientation,
			title,
		} = setup;

		this.assign(data, {
			blurb,
			cart,
			eventId,
			items,
			layout,
			mobileLayout,
			mobileOrientation,
			mode,
			moreInfoHidden,
			orientation,
			title,
			queryParams: this.App.getQueryParams(),
		});

		if (this.isInt(eventId)) {
			return Events.get({
				id: eventId,
				then: (event) => (data.event = event),
			});
		}
	},
	constructor() {
		this.addClass(`unseen`);

		this.setContainerAttributes();

		return this.reconcile();
	},
	ready() {
		const { App, dimensions } = this;
		const scrollHandler = () => {
			if (dimensions.inViewport) {
				App.offScroll(scrollHandler);
				App.offTick(scrollHandler);
				this.removeClass(`unseen`);
			}
		};

		App.onTick(scrollHandler);
		App.onScroll(scrollHandler);
	},
	api: {
		/**
		 * Sets the container attributes based on the current data. The attributes are passed in through the setup object
		 * when the module is initialized.
		 * @method setContainerAttributes
		 */
		setContainerAttributes: {
			value() {
				const { container, data } = this;
				const { layout, mobileLayout, mobileOrientation, mode, orientation } = data;

				container.setAttribute(`mode`, mode);
				container.setAttribute(`data-layout`, layout);
				container.setAttribute(`data-mobile-layout`, mobileLayout);
				container.setAttribute(`data-orientation`, orientation);
				container.setAttribute(`data-mobile-orientation`, mobileOrientation);
			},
		},

		/**
		 * Reconciles the carousel by creating card instances for each item in the carousel.
		 * It checks the event and collection configurations to determine which items should be included.
		 * It also handles the visibility of items based on the query parameters and the event's sales.
		 * @method reconcile
		 * @returns {Promise} A promise that resolves when all card instances are created and the scroll content is initialized.
		 */
		reconcile: {
			value() {
				const { data } = this;
				const { event, eventId, items, mobileOrientation, mode, moreInfoHidden, orientation } = data;
				const promises = [];
				let version = { desktop: `legacy`, mobile: `legacy` };

				this.each(items, (item, i) => {
					const { id, type } = item;

					if (this.isObject(event) && type === `COLLECTION`) {
						const { collectionConfiguration } = event;
						const { collections } = collectionConfiguration;
						const collection = this.find(collections, { id });

						if (version.desktop === `legacy`) {
							const desktopUrl = orientation === `PORTRAIT` ? collection.portraitImageUrl : collection.imageUrlv2;
							if (this.isString(desktopUrl, 1)) {
								version.desktop = `ux4`;
							}
						}
						if (version.mobile === `legacy`) {
							const mobileUrl = mobileOrientation === `PORTRAIT` ? collection.portraitImageUrl : collection.imageUrlv2;
							if (this.isString(mobileUrl, 1)) {
								version.mobile = `ux4`;
							}
						}
					}
				});
				this.each(items, (item, i) => {
					const { id, type } = item;
					const name = `card-${i}`;
					const imageMode = `simple`;
					const setup = { imageMode, item, mobileOrientation, mode, moreInfoHidden, name, orientation };
					let namespace;

					if (this.isObject(event) && type === `COLLECTION`) {
						const { queryParams } = data;
						const { collectionConfiguration } = event;
						const { collections } = collectionConfiguration;
						const collection = this.find(collections, { id });
						const itemWrapper = this.get(`[wrapper-index="${i}"]`);
						let includeCollection = true;

						// Check if the query parameters excludes this collection
						includeCollection =
							includeCollection &&
							!this.CollectionUtils.excludeByQueryParams({
								queryParams,
								collectionId: id,
							});

						// Check if the collection should be excluded based on its behaviors
						includeCollection =
							includeCollection && !this.CollectionUtils.excludeByCollectionBehavior({ collection, event });

						if (!includeCollection) {
							this.DOM.remove(itemWrapper);
							return; // skip this card
						}

						const desktop =
							(orientation === `PORTRAIT` ? collection.portraitImageUrl : collection.imageUrlv2) || collection.imageUrl;
						const mobile =
							(mobileOrientation === `PORTRAIT` ? collection.portraitImageUrl : collection.imageUrlv2) ||
							collection.imageUrl;

						setup.item = {
							...collection,
							...item,
							imageUrls: [{ desktop, mobile }],
						};
					}

					switch (type) {
						case `COLLECTION`:
							namespace = `Modules/CollectionCard`;
							setup.cart = data.cart;
							setup.eventId = eventId;
							setup.version = version;
							break;

						case `SALE`:
							// NOTE: untested, need to verify setup arguments and possibly update
							namespace = `Modules/ProductCard`;
							setup.eventId = eventId;
							setup.version = version;
							break;

						case `GROUP_SALE`:
							namespace = `Modules/Card`;
							setup.version = { desktop: `ux4`, mobile: `ux4` };
							break;

						default:
							namespace = `Modules/Card`;
							setup.version = version;
					}

					promises.push(this.create({ namespace, setup }));
				});

				data.length = promises.length;

				return this.when({
					promises,
					then: () =>
						this.create({
							namespace: `Widgets/ScrollContent2`,
							setup: {
								name: `scroll-content`,
								container: this.get(`[name="scroll-content"]`),
								arrowMode: `edges-circled`,
								theme: data.theme === `DARK` ? `LIGHT` : `DARK`,
							},
						}),
				});
			},
		},

		getLength: {
			public: true,
			value(context) {
				return this.data.length;
			},
		},

		updateCart: {
			public: true,
			value(context, cart) {
				const { data, instances } = this;

				data.cart = cart;

				this.each(instances, (instance) => {
					if (this.isObject(instance) && this.isFunction(instance.updateCart)) {
						instance.updateCart(cart);
					}
				});
			},
		},
	},
});

define("Modules/Carousel.templates",["handlebars"], function(Handlebars) {

this["Templates"] = this["Templates"] || {};

this["Templates"]["Modules/Carousel/main"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<h2 class=\"title\">"
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"title") : depth0), depth0))
    + "</h2>";
},"3":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<h3 class=\"blurb\">"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"blurb") : depth0), depth0)) != null ? stack1 : "")
    + "</h3>";
},"5":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <li wrapper-index=\""
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + "\"> <div name=\"card-"
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + "\"></div> </li> ";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, alias3=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"title") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":1,"column":52}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"blurb") : depth0),{"name":"if","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":53},"end":{"line":1,"column":107}}})) != null ? stack1 : "")
    + " <div name=\"scroll-content\" class=\"desktop-layout-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"layout") : depth0), depth0))
    + " mobile-layout-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"mobileLayout") : depth0), depth0))
    + "\"> <ul class=\"list\"> "
    + ((stack1 = lookupProperty(helpers,"each").call(alias1,(depth0 != null ? lookupProperty(depth0,"items") : depth0),{"name":"each","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":219},"end":{"line":1,"column":317}}})) != null ? stack1 : "")
    + " </ul> </div>";
},"useData":true});

return this["Templates"];

});
//# sourceMappingURL=Carousel.js.map